import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Card, CardContent, Typography, Divider,
  Fade, Grow, useTheme, useMediaQuery, IconButton, Slide,
  Tooltip,
} from '@mui/material';
import LogoutButton from './LogoutButton';
import UploadPlan from './UploadPlan';
import ViewCompletedOperations from './ViewCompletedOperations';

import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Upload, CheckCircle } from 'lucide-react';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ height: '100%' }}>
      {value === index && (
        <Fade in={value === index} timeout={300}>
          <Box sx={{ p: { xs: 1, md: 3 }, height: '100%' }}>{children}</Box>
        </Fade>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

export default function ModernDashboard() {
  const [value, setValue] = React.useState(0);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const location = useLocation();
  const { username } = location.state;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const tabsData = [
    {
      label: 'Upload Plan',
      icon: Upload,
      description: 'Create and upload new business plans'
    },
    {
      label: 'Completed Operations',
      icon: CheckCircle,
      description: 'View your finished operations'
    }
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: '100vh',
        overflow: 'hidden',
        bgcolor: 'background.default',
        p: 2,
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {/* Icon-Only Sidebar */}
      <Box
        sx={{
          flexShrink: 0,
          width: isDesktop ? (sidebarOpen ? 90 : 0) : '100%',
          height: { xs: 'auto', md: '100%' },
          overflow: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          position: 'relative',
        }}
      >
        <Slide direction="right" in={sidebarOpen} mountOnEnter unmountOnExit>
          <Card
            elevation={3}
            sx={{
              width: 90,
              height: '100%',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ 
              p: 1.5, 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            }}>
              {/* Compact Header */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.9rem' }}>
                  {username.charAt(0).toUpperCase()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  Menu
                </Typography>
              </Box>

              {/* Icon-Only Navigation Buttons */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2, 
                mb: 2,
                overflowY: 'auto',
                flex: '1 1 auto',
                alignItems: 'center',
              }}>
                {tabsData.map((tab, index) => {
                  const IconComponent = tab.icon;
                  return (
                    <Grow in key={index} timeout={300 + index * 100}>
                      <Tooltip 
                        title={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {tab.label}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                              {tab.description}
                            </Typography>
                          </Box>
                        } 
                        placement="right" 
                        arrow
                      >
                        <IconButton
                          onClick={() => setValue(index)}
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 2,
                            transition: 'all 0.3s ease-in-out',
                            border: value === index ? '2px solid' : '1px solid',
                            borderColor: value === index ? 'primary.main' : 'divider',
                            bgcolor: value === index ? 'primary.main' : 'background.paper',
                            '&:hover': {
                              bgcolor: value === index ? 'primary.dark' : 'action.hover',
                              transform: 'scale(1.08)',
                              boxShadow: 3,
                            },
                          }}
                        >
                          <IconComponent 
                            size={26} 
                            color={value === index ? '#fff' : theme.palette.text.secondary}
                          />
                        </IconButton>
                      </Tooltip>
                    </Grow>
                  );
                })}
              </Box>

              {/* Compact Logout */}
              <Box sx={{ mt: 'auto', flexShrink: 0 }}>
                <LogoutButton username={username} />
              </Box>
            </CardContent>
          </Card>
        </Slide>
      </Box>

      {/* Minimalist Toggle Button - Desktop */}
      {isDesktop && (
        <IconButton 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          sx={{
            position: 'absolute',
            top: '50%',
            left: sidebarOpen ? 90 : 0,
            transform: 'translate(-50%, -50%)',
            zIndex: 1300,
            backgroundColor: theme.palette.mode === 'dark' 
              ? theme.palette.grey[800] 
              : theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 2,
            width: 36,
            height: 36,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? theme.palette.grey[700] 
                : theme.palette.grey[100],
              transform: 'translate(-50%, -50%) scale(1.1)',
              boxShadow: 4,
            },
          }}
        >
          {sidebarOpen ? (
            <ChevronLeft 
              size={20} 
              color={theme.palette.mode === 'dark' ? '#fff' : '#333'} 
            />
          ) : (
            <ChevronRight 
              size={20} 
              color={theme.palette.mode === 'dark' ? '#fff' : '#333'} 
            />
          )}
        </IconButton>
      )}

      {/* Mobile Toggle Button */}
      {isMobile && (
        <IconButton
          onClick={() => setSidebarOpen(!sidebarOpen)}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 1300,
            backgroundColor: theme.palette.mode === 'dark' 
              ? theme.palette.grey[800] 
              : theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 2,
            width: 40,
            height: 40,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? theme.palette.grey[700] 
                : theme.palette.grey[100],
              transform: 'scale(1.1)',
              boxShadow: 4,
            },
          }}
        >
          {sidebarOpen ? (
            <ChevronLeft 
              size={22} 
              color={theme.palette.mode === 'dark' ? '#fff' : '#333'} 
            />
          ) : (
            <ChevronRight 
              size={22} 
              color={theme.palette.mode === 'dark' ? '#fff' : '#333'} 
            />
          )}
        </IconButton>
      )}

      {/* Divider */}
      <Divider orientation="vertical" flexItem sx={{ 
        display: { xs: 'none', md: sidebarOpen ? 'block' : 'none' }, 
        mx: 1 
      }} />

      {/* Main Content */}
      <Card sx={{ 
        flex: '1 1 auto',
        height: '100%',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        position: 'relative',
      }}>
        <TabPanel value={value} index={0}>
          <UploadPlan />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <ViewCompletedOperations />
        </TabPanel>
      </Card>
    </Box>
  );
}